<template>
  <div class="bg-gray-50">
    <Hero />

    <!-- Explore Grid -->
    <section class="container mx-auto px-4 py-16">
      <div class="flex flex-col items-center text-center mb-12">
        <p class="text-sm uppercase tracking-[0.5em] text-amber-500">Explore</p>
        <h2 class="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Everything for Your Umrah Journey</h2>
        <p class="mt-4 max-w-2xl text-gray-600">Umrah packages, hotel bookings, flights, and guided Ziarat tours — all in one place.</p>
      </div>
      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <router-link
          v-for="item in exploreLinks"
          :key="item.title"
          :to="item.path"
          class="group flex flex-col items-center rounded-[24px] border border-gray-100 bg-white p-6 text-center shadow-sm transition hover:-translate-y-1 hover:border-amber-200 hover:shadow-lg"
        >
          <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition group-hover:bg-amber-100">
            <svg class="h-7 w-7" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" :d="item.icon" />
            </svg>
          </div>
          <h3 class="mt-4 font-semibold text-gray-900">{{ item.title }}</h3>
          <p class="mt-1 text-xs text-gray-500">{{ item.description }}</p>
        </router-link>
      </div>
    </section>

    <!-- Featured Packages Preview -->
    <FeaturedPackages />
    <div class="flex justify-center pb-16 -mt-8">
      <router-link
        to="/packages"
        class="inline-flex items-center gap-2 rounded-full bg-gray-900 px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-gray-800"
      >
        View All Packages &amp; Pricing
        <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </router-link>
    </div>

    <!-- About Teaser -->
    <AboutSection />

    <!-- Final CTA -->
    <section class="bg-gray-900 py-16 text-center text-white">
      <div class="container mx-auto px-4">
        <h2 class="text-3xl font-bold md:text-4xl">Ready to Begin Your Journey?</h2>
        <p class="mt-3 max-w-xl mx-auto text-gray-300">Talk to our concierge team about packages, hotels, flights, and Ziarat tours tailored to your family.</p>
        <router-link
          to="/contact"
          class="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600"
        >
          Contact Us
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
import Hero from '../components/Hero.vue'
import FeaturedPackages from '../components/sections/FeaturedPackages.vue'
import AboutSection from '../components/sections/AboutSection.vue'

export default {
  name: 'HomePage',
  components: {
    Hero,
    FeaturedPackages,
    AboutSection
  },
  data() {
    return {
      exploreLinks: [
        {
          title: 'Umrah Packages',
          description: 'Month-wise pricing',
          path: '/packages',
          icon: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0'
        },
        {
          title: 'Hotels',
          description: 'Makkah & Madina stays',
          path: '/hotels',
          icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21'
        },
        {
          title: 'Flights & Visa',
          description: 'Any-country departures',
          path: '/flights',
          icon: 'M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.874L5.999 12zm0 0h7.5'
        },
        {
          title: 'Haramain Ziarat',
          description: 'Guided historical tours',
          path: '/ziarat',
          icon: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'
        },
        {
          title: 'Other Services',
          description: 'Transport & visa help',
          path: '/services',
          icon: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z'
        }
      ]
    }
  }
}
</script>
